/*
 * Blinc B2B — Wholesale Consultation Funnel
 * ------------------------------------------------------------
 * Controls the consultation popup, the embedded Calendly step, the
 * persistent CTA, frequency capping and tracking hooks.
 *
 * Loaded by sections/consultation-funnel.liquid (defer). Runs after the
 * theme bundle (theme.min.js) because both scripts are deferred and this
 * one appears later in the document.
 *
 * Integration points with the Impulse theme (read-only):
 *   - theme.Modals   → open/close, focus trap, Escape, backdrop click
 *   - theme.sections → editor select/deselect support
 *   - html.modal-open / body.js-drawer-open → used to avoid stacking overlays
 * If the theme bundle is unavailable, a minimal fallback modal is used.
 *
 * Tracking: DOM CustomEvents on `document` and a mirrored push to
 * window.dataLayer when it already exists. No PII is ever included.
 */
(function () {
  'use strict';

  var KEY_PREFIX = 'blinc_cf_';
  var CALENDLY_ORIGIN = 'https://calendly.com';
  var MOBILE_QUERY = '(max-width: 768px)';
  var OVERLAY_RETRY_MS = 1500;
  var OVERLAY_RETRY_MAX = 8;
  /* html class present only while the consultation modal is open (used by CSS to hide the UserWay launcher) */
  var OPEN_CLASS = 'cf-consultation-open';
  /* theme.Modals keeps .modal--is-closing for 500ms while it animates out; reset the step only after that */
  var MODAL_CLOSE_MS = 600;

  /* ------------------------------------------------------------------
   * Storage (frequency capping only). localStorage → cookie → memory.
   * ------------------------------------------------------------------ */
  var memoryStore = {};

  function storageFor(kind) {
    var api;
    try {
      api = window[kind];
      var probe = KEY_PREFIX + 'probe';
      api.setItem(probe, '1');
      api.removeItem(probe);
    } catch (e) {
      api = null;
    }
    return {
      get: function (key) {
        var k = KEY_PREFIX + key;
        if (api) {
          try { return api.getItem(k); } catch (e) { /* fall through */ }
        }
        if (window.Cookies && typeof window.Cookies.get === 'function') {
          var c = window.Cookies.get(k);
          if (typeof c !== 'undefined') return c;
        }
        return Object.prototype.hasOwnProperty.call(memoryStore, k) ? memoryStore[k] : null;
      },
      set: function (key, value, expiresDays) {
        var k = KEY_PREFIX + key;
        if (api) {
          try { api.setItem(k, String(value)); return; } catch (e) { /* fall through */ }
        }
        if (window.Cookies && typeof window.Cookies.set === 'function') {
          var opts = { path: '/', sameSite: 'Lax' };
          if (expiresDays) opts.expires = expiresDays;
          window.Cookies.set(k, String(value), opts);
          return;
        }
        memoryStore[k] = String(value);
      },
      remove: function (key) {
        var k = KEY_PREFIX + key;
        if (api) { try { api.removeItem(k); } catch (e) { /* ignore */ } }
        if (window.Cookies && typeof window.Cookies.remove === 'function') {
          window.Cookies.remove(k, { path: '/' });
        }
        delete memoryStore[k];
      }
    };
  }

  var local = storageFor('localStorage');
  var session = storageFor('sessionStorage');

  /* ------------------------------------------------------------------
   * Helpers
   * ------------------------------------------------------------------ */
  function now() { return Date.now(); }
  function daysToMs(d) { return Number(d) * 24 * 60 * 60 * 1000; }
  function toBool(v) { return v === true || v === 'true' || v === '1'; }
  function isMobile() {
    if (window.theme && window.theme.config && typeof window.theme.config.bpSmall === 'boolean') {
      return window.theme.config.bpSmall;
    }
    return window.matchMedia(MOBILE_QUERY).matches;
  }
  function isDesignMode() { return !!(window.Shopify && window.Shopify.designMode); }
  function isVisible(el) {
    if (!el) return false;
    if (el.hidden) return false;
    var style = window.getComputedStyle(el);
    return style.display !== 'none' && style.visibility !== 'hidden' && el.getClientRects().length > 0;
  }

  /* Klaviyo signup popup state, read from its live DOM only (no Klaviyo API calls):
     body.klaviyo-prevent-body-scrolling while open, and/or a displayed Klaviyo
     role="dialog" whose [data-testid="POPUP"] box has height. The teaser bubble
     is not a popup and is ignored here. */
  function klaviyoPopupOpen() {
    if (document.body && document.body.classList.contains('klaviyo-prevent-body-scrolling')) return true;
    var dialogs = document.querySelectorAll('[role="dialog"][class*="kl-private-reset"]');
    for (var i = 0; i < dialogs.length; i++) {
      var d = dialogs[i];
      if (window.getComputedStyle(d).display === 'none') continue;
      var box = d.querySelector('[data-testid="POPUP"]');
      if (box && box.getBoundingClientRect().height > 0) return true;
    }
    return false;
  }

  /* Respect Shopify's consent API if the store exposes it. */
  function analyticsAllowed() {
    try {
      var cp = window.Shopify && window.Shopify.customerPrivacy;
      if (cp && typeof cp.analyticsProcessingAllowed === 'function') {
        return cp.analyticsProcessingAllowed() !== false;
      }
    } catch (e) { /* ignore */ }
    return true;
  }

  /* DOM CustomEvent + dataLayer mirror. `detail` must never contain PII. */
  function track(name, detail) {
    var payload = {};
    Object.keys(detail || {}).forEach(function (k) { payload[k] = detail[k]; });
    try {
      document.dispatchEvent(new CustomEvent(name, { detail: payload, bubbles: false }));
    } catch (e) { /* ignore */ }
    if (Array.isArray(window.dataLayer) && analyticsAllowed()) {
      var row = { event: name };
      Object.keys(payload).forEach(function (k) { row[k] = payload[k]; });
      window.dataLayer.push(row);
    }
  }

  /* ------------------------------------------------------------------
   * Minimal modal fallback (only used if theme.Modals is missing)
   * ------------------------------------------------------------------ */
  function FallbackModal(id) {
    this.id = id;
    this.modal = document.getElementById(id);
    this.modalIsOpen = false;
    if (!this.modal) return;
    var self = this;
    this.modal.querySelectorAll('.js-modal-close').forEach(function (btn) {
      btn.addEventListener('click', function () { self.close(); });
    });
    this.modal.addEventListener('click', function (evt) {
      if (!evt.target.closest('.modal__inner')) self.close();
    });
    this._onKey = function (evt) { if (evt.key === 'Escape') self.close(); };
  }
  FallbackModal.prototype.open = function () {
    if (!this.modal || this.modalIsOpen) return;
    this.modal.classList.add('modal--is-active');
    document.documentElement.classList.add('modal-open');
    this.modalIsOpen = true;
    document.addEventListener('keyup', this._onKey);
    var focusTarget = this.modal.querySelector('[data-cf-card]') || this.modal;
    try { focusTarget.focus(); } catch (e) { /* ignore */ }
    document.dispatchEvent(new CustomEvent('modalOpen.' + this.id));
  };
  FallbackModal.prototype.close = function () {
    if (!this.modal || !this.modalIsOpen) return;
    this.modal.classList.remove('modal--is-active');
    document.documentElement.classList.remove('modal-open');
    this.modalIsOpen = false;
    document.removeEventListener('keyup', this._onKey);
    document.dispatchEvent(new CustomEvent('modalClose.' + this.id));
  };

  function createModal(id) {
    if (window.theme && typeof window.theme.Modals === 'function') {
      var m = new window.theme.Modals(id, 'consultation-funnel', {
        closeOffContentClick: true,
        focusIdOnOpen: id + '-card'
      });
      if (m && m.modal) return m;
    }
    return new FallbackModal(id);
  }

  /* ------------------------------------------------------------------
   * Section controller
   * ------------------------------------------------------------------ */
  function ConsultationFunnel(container) {
    this.container = container;
    if (!container) return;

    var d = container.dataset;
    this.config = {
      modalId: d.modalId,
      audience: d.audience === 'returning' ? 'returning' : 'general',
      enabled: toBool(d.enabled),
      autoOpen: d.autoOpen === undefined ? true : toBool(d.autoOpen), /* false for logged-out visitors by default */
      testMode: toBool(d.testMode),
      delaySeconds: Math.max(0, Number(d.delaySeconds) || 0),
      scrollPercent: Math.min(100, Math.max(0, Number(d.scrollPercent) || 0)),
      dismissDays: Math.max(1, Number(d.dismissDays) || 14),
      bookedDays: Math.max(1, Number(d.bookedDays) || 30),
      mobileEnabled: toBool(d.mobileEnabled),
      ctaEnabled: toBool(d.ctaEnabled),
      ctaSessionHide: toBool(d.ctaSessionHide),
      calendlyUrl: d.calendlyUrl || '',
      calendlyOk: toBool(d.calendlyOk),
      prefillName: d.prefillName || '',
      prefillEmail: d.prefillEmail || ''
    };

    this.modalEl = document.getElementById(this.config.modalId);
    if (!this.modalEl) return;

    this.card = this.modalEl.querySelector('[data-cf-card]');
    this.calendlyPanel = this.modalEl.querySelector('[data-cf-calendly]');
    this.frame = this.modalEl.querySelector('[data-cf-frame]');
    this.loading = this.modalEl.querySelector('[data-cf-loading]');
    this.ctaWrap = container.querySelector('[data-cf-cta]');

    this.opened = false;
    this.openSource = null;
    this.bookedThisOpen = false;
    this.timer = null;
    this.resetTimer = null;
    this.klaviyoObserver = null;
    this.klaviyoWatch = null;   /* observer active only while the consultation is open */
    this.systemClose = false;   /* true when the funnel closes itself to yield to Klaviyo */
    this.overlayRetries = 0;
    this.scrollHandler = null;

    this.modal = createModal(this.config.modalId);

    this.bindEvents();
    this.applyBreakpoint();
    this.schedule();
  }

  ConsultationFunnel.prototype = {

    /* ---------------- state helpers ---------------- */
    base: function (extra) {
      var out = {
        audience: this.config.audience,
        device: isMobile() ? 'mobile' : 'desktop'
      };
      Object.keys(extra || {}).forEach(function (k) { out[k] = extra[k]; });
      return out;
    },

    isSuppressed: function () {
      if (this.config.testMode) return false;
      var until = Math.max(Number(local.get('dismissed_until')) || 0, Number(local.get('booked_until')) || 0);
      return until > now();
    },

    markDismissed: function () {
      if (this.config.testMode || isDesignMode()) return;
      local.set('dismissed_until', now() + daysToMs(this.config.dismissDays), this.config.dismissDays);
    },

    markBooked: function () {
      if (isDesignMode()) return;
      local.set('booked_until', now() + daysToMs(this.config.bookedDays), this.config.bookedDays);
    },

    otherOverlayOpen: function () {
      var html = document.documentElement;
      if (html.classList.contains('modal-open') && !this.modal.modalIsOpen) return true;
      if (html.classList.contains('js-drawer-open') || document.body.classList.contains('js-drawer-open')) return true;
      /* Best-effort detection of third-party overlays (OptiMonk, BSS warnings). */
      var candidates = document.querySelectorAll('.om-holder, .om-overlay, [class*="optimonk"], .bss-modal-warning-amo-wrap.active, .bss-custom-modal-container');
      for (var i = 0; i < candidates.length; i++) {
        if (isVisible(candidates[i])) return true;
      }
      return false;
    },

    /* ---------------- responsive text swaps ---------------- */
    applyBreakpoint: function () {
      var mobile = isMobile();
      this.container.querySelectorAll('[data-cf-desktop]').forEach(function (el) { el.hidden = mobile; });
      this.container.querySelectorAll('[data-cf-mobile]').forEach(function (el) { el.hidden = !mobile; });
    },

    /* ---------------- scheduling ---------------- */
    schedule: function () {
      var self = this;

      if (!this.config.enabled) return;

      if (isDesignMode()) {
        /* In the theme editor: never auto-open, but show the CTA so it can be styled. */
        this.showCta();
        return;
      }

      /* No automatic popup for this visitor (logged out): persistent CTA only. Manual
         opens via the CTA, the Custom Kits link or #book-consultation still work. */
      if (!this.config.autoOpen) { this.showCta(); return; }
      if (isMobile() && !this.config.mobileEnabled) { this.showCta(); return; }
      if (this.isSuppressed()) { this.showCta(); return; }
      if (!this.config.testMode && session.get('auto_shown')) { this.showCta(); return; }

      this.armTriggers();
    },

    /* Existing delay + scroll-depth triggers. Called on load and re-armed
       from scratch once a Klaviyo popup that blocked the auto-open closes. */
    armTriggers: function () {
      var self = this;
      this.clearTriggers();
      this.overlayRetries = 0;

      this.timer = window.setTimeout(function () { self.tryAutoOpen(); }, this.config.delaySeconds * 1000);

      if (this.config.scrollPercent > 0) {
        this.scrollHandler = function () {
          var doc = document.documentElement;
          var scrollable = Math.max(0, (doc.scrollHeight || document.body.scrollHeight) - window.innerHeight);
          if (scrollable < 200) return; /* page too short for a meaningful depth reading */
          var depth = (window.pageYOffset || doc.scrollTop) / scrollable * 100;
          if (depth >= self.config.scrollPercent) self.tryAutoOpen();
        };
        window.addEventListener('scroll', this.scrollHandler, { passive: true });
      }
    },

    clearTriggers: function () {
      if (this.timer) { window.clearTimeout(this.timer); this.timer = null; }
      if (this.scrollHandler) { window.removeEventListener('scroll', this.scrollHandler); this.scrollHandler = null; }
    },

    tryAutoOpen: function () {
      var self = this;
      if (this.opened || this.modal.modalIsOpen) { this.clearTriggers(); return; }
      if (klaviyoPopupOpen()) { this.clearTriggers(); this.deferUntilKlaviyoCloses(); return; }
      if (this.otherOverlayOpen()) {
        if (this.overlayRetries++ < OVERLAY_RETRY_MAX) {
          window.setTimeout(function () { self.tryAutoOpen(); }, OVERLAY_RETRY_MS);
        } else {
          this.clearTriggers();
          this.showCta();
        }
        return;
      }
      this.clearTriggers();
      if (!this.config.testMode) session.set('auto_shown', '1');
      this.open('popup');
    },

    /* Klaviyo-first: never auto-open over the Klaviyo signup popup. Watch its DOM
       signals with a MutationObserver (no polling) and, once it closes, re-arm the
       normal delay and scroll triggers from that moment. Nothing is written to
       storage here, so neither experience is suppressed by the other. */
    deferUntilKlaviyoCloses: function () {
      var self = this;
      if (this.klaviyoObserver) return; /* already waiting */
      var observer = new MutationObserver(function () {
        if (klaviyoPopupOpen()) return;
        observer.disconnect();
        self.klaviyoObserver = null;
        if (self.opened || self.modal.modalIsOpen) return;
        self.armTriggers();
      });
      this.klaviyoObserver = observer;
      observer.observe(document.body, { attributes: true, attributeFilter: ['class'], childList: true });
      document.querySelectorAll('[role="dialog"][class*="kl-private-reset"]').forEach(function (d) {
        observer.observe(d, { attributes: true, attributeFilter: ['style', 'class'] });
      });
    },

    /* ---------------- open / close ---------------- */
    open: function (source, force) {
      if (this.modal.modalIsOpen) return;
      if (!force && this.otherOverlayOpen()) return;

      /* A pending post-close reset would otherwise wipe a direct-to-Calendly open */
      if (this.resetTimer) { window.clearTimeout(this.resetTimer); this.resetTimer = null; }

      this.opened = true;
      this.openSource = source || 'popup';
      this.bookedThisOpen = false;
      this.systemClose = false;
      this.showIntro();
      this.applyBreakpoint();
      this.modal.open();
      document.documentElement.classList.add(OPEN_CLASS);
      this.watchKlaviyoWhileOpen();
      this.hideCta(false);
      track('consultation:open', this.base({ source: this.openSource }));
    },

    onClosed: function () {
      var self = this;
      var yielded = this.systemClose;
      this.systemClose = false;
      document.documentElement.classList.remove(OPEN_CLASS);
      this.stopKlaviyoWatch();
      if (!yielded && !this.bookedThisOpen) {
        this.markDismissed();
        track('consultation:dismiss', this.base({ source: this.openSource || 'popup' }));
      }
      /* Keep the current step (e.g. Calendly) on screen while the modal animates out.
         Reset to the intro only once it is fully hidden so nothing flashes. */
      if (this.resetTimer) window.clearTimeout(this.resetTimer);
      this.resetTimer = window.setTimeout(function () {
        self.resetTimer = null;
        if (!self.modal.modalIsOpen) self.showIntro();
      }, MODAL_CLOSE_MS);
      if (yielded) {
        /* Yielded to Klaviyo: not a dismissal. Keep the visitor eligible (including the
           per-session auto-open flag) and re-arm the normal triggers once Klaviyo closes. */
        this.opened = false;
        if (!this.config.testMode) session.remove('auto_shown');
        this.deferUntilKlaviyoCloses();
        return;
      }
      this.showCta();
    },

    /* Klaviyo priority while the consultation is open: if the full Klaviyo signup dialog
       becomes visible, close the consultation gracefully (system close, not a dismissal).
       Skipped in the theme editor so section select/deselect can never loop. */
    watchKlaviyoWhileOpen: function () {
      var self = this;
      if (isDesignMode() || this.klaviyoWatch) return;
      var watch = new MutationObserver(function () {
        if (!self.modal.modalIsOpen || !klaviyoPopupOpen()) return;
        self.stopKlaviyoWatch();
        self.systemClose = true;
        self.modal.close();
      });
      this.klaviyoWatch = watch;
      watch.observe(document.body, { attributes: true, attributeFilter: ['class'], childList: true });
      document.querySelectorAll('[role="dialog"][class*="kl-private-reset"]').forEach(function (d) {
        watch.observe(d, { attributes: true, attributeFilter: ['style', 'class'] });
      });
    },

    stopKlaviyoWatch: function () {
      if (this.klaviyoWatch) { this.klaviyoWatch.disconnect(); this.klaviyoWatch = null; }
    },

    showIntro: function () {
      if (this.card) this.card.classList.remove('cf-modal__card--calendly');
      if (this.calendlyPanel) this.calendlyPanel.hidden = true;
    },

    showCalendly: function () {
      if (this.card) this.card.classList.add('cf-modal__card--calendly');
      if (this.calendlyPanel) this.calendlyPanel.hidden = false;
    },

    /* ---------------- Calendly ---------------- */
    buildCalendlyUrl: function () {
      var url = this.config.calendlyUrl;
      var params = [];
      params.push('embed_domain=' + encodeURIComponent(window.location.hostname));
      params.push('embed_type=Inline');
      params.push('hide_gdpr_banner=1');
      if (this.config.prefillName) params.push('name=' + encodeURIComponent(this.config.prefillName));
      if (this.config.prefillEmail) params.push('email=' + encodeURIComponent(this.config.prefillEmail));
      params.push('utm_source=' + encodeURIComponent('blinc-b2b'));
      params.push('utm_medium=' + encodeURIComponent('consultation-' + (this.openSource || 'popup')));
      return url + (url.indexOf('?') === -1 ? '?' : '&') + params.join('&');
    },

    /* skipClickTrack: the trigger already emitted its own cta_click (direct-to-Calendly links) */
    book: function (skipClickTrack) {
      var self = this;
      if (!skipClickTrack) {
        track('consultation:cta_click', this.base({ source: this.openSource || 'popup', cta: 'primary' }));
      }

      if (!this.config.calendlyOk || !this.frame) {
        /* Fallback: open Calendly in a new tab if the URL is missing/invalid. */
        if (this.config.calendlyUrl) window.open(this.config.calendlyUrl, '_blank', 'noopener');
        return;
      }

      this.showCalendly();
      if (!this.frame.getAttribute('src')) {
        if (this.loading) this.loading.hidden = false;
        this.frame.addEventListener('load', function () {
          if (self.loading) self.loading.hidden = true;
        }, { once: true });
        this.frame.setAttribute('src', this.buildCalendlyUrl());
      }
      track('consultation:calendly_open', this.base({ source: this.openSource || 'popup' }));
    },

    onCalendlyMessage: function (evt) {
      if (evt.origin !== CALENDLY_ORIGIN) return;
      var data = evt.data;
      if (!data || typeof data.event !== 'string' || data.event.indexOf('calendly.') !== 0) return;
      if (!this.frame || !this.frame.getAttribute('src')) return;

      switch (data.event) {
        case 'calendly.date_and_time_selected':
          track('consultation:calendly_time_selected', this.base({ source: this.openSource || 'popup' }));
          break;
        case 'calendly.event_scheduled':
          this.bookedThisOpen = true;
          this.markBooked();
          track('consultation:booked', this.base({ source: this.openSource || 'popup' }));
          break;
        default:
          break;
      }
    },

    /* ---------------- persistent CTA ---------------- */
    showCta: function () {
      if (!this.ctaWrap || !this.config.ctaEnabled) return;
      if (this.config.ctaSessionHide && session.get('cta_hidden') && !isDesignMode()) return;
      this.ctaWrap.hidden = false;
    },

    hideCta: function (forSession) {
      if (!this.ctaWrap) return;
      this.ctaWrap.hidden = true;
      if (forSession && this.config.ctaSessionHide) session.set('cta_hidden', '1');
    },

    /* ---------------- events ---------------- */
    bindEvents: function () {
      var self = this;

      document.addEventListener('modalClose.' + this.config.modalId, function () { self.onClosed(); });

      /* Any trigger anywhere on the page: persistent CTA, Custom Kits module, or a nav link to #book-consultation */
      document.addEventListener('click', function (evt) {
        var trigger = evt.target.closest('[data-consultation-open], a[href="#book-consultation"]');
        if (!trigger) return;
        var direct = trigger.getAttribute('data-cf-direct') === 'calendly';
        var source = trigger.getAttribute('data-cf-source') || 'link';

        /* Direct-to-Calendly trigger with no usable embed: let the link's own href (new tab) take over. */
        if (direct && (!self.config.calendlyOk || !self.frame)) {
          track('consultation:cta_click', self.base({ source: source, cta: 'trigger' }));
          return;
        }

        evt.preventDefault();
        if (source !== 'popup') {
          track('consultation:cta_click', self.base({ source: source, cta: 'trigger' }));
        }
        if (self.modal.modalIsOpen) return;
        self.open(source, true);
        if (direct) self.book(true); /* skip the intro screen, go straight to date and time selection */
      });

      var bookBtn = this.modalEl.querySelector('[data-cf-book]');
      if (bookBtn) bookBtn.addEventListener('click', function () { self.book(); });

      var backBtn = this.modalEl.querySelector('[data-cf-back]');
      if (backBtn) backBtn.addEventListener('click', function () { self.showIntro(); });

      var secondary = this.modalEl.querySelector('[data-cf-secondary]');
      if (secondary) {
        secondary.addEventListener('click', function () {
          track('consultation:cta_click', self.base({ source: self.openSource || 'popup', cta: 'secondary' }));
          /* If it is a link, record the dismissal now because navigation may interrupt the close handler. */
          if (secondary.tagName === 'A') self.markDismissed();
        });
      }

      var hideBtn = this.container.querySelector('[data-cf-cta-hide]');
      if (hideBtn) hideBtn.addEventListener('click', function () { self.hideCta(true); });

      this._onMessage = function (evt) { self.onCalendlyMessage(evt); };
      window.addEventListener('message', this._onMessage);

      var mql = window.matchMedia(MOBILE_QUERY);
      this._onMql = function () { self.applyBreakpoint(); };
      if (typeof mql.addEventListener === 'function') mql.addEventListener('change', this._onMql);
      else if (typeof mql.addListener === 'function') mql.addListener(this._onMql);
    },

    /* ---------------- theme editor hooks ---------------- */
    onSelect: function () { this.open('popup', true); },
    onDeselect: function () { if (this.modal.modalIsOpen) this.modal.close(); },
    onUnload: function () {
      this.clearTriggers();
      if (this.klaviyoObserver) { this.klaviyoObserver.disconnect(); this.klaviyoObserver = null; }
      this.stopKlaviyoWatch();
      if (this.resetTimer) { window.clearTimeout(this.resetTimer); this.resetTimer = null; }
      document.documentElement.classList.remove(OPEN_CLASS);
      if (this._onMessage) window.removeEventListener('message', this._onMessage);
      if (this.modal.modalIsOpen) this.modal.close();
    }
  };

  /* ------------------------------------------------------------------
   * Boot: register with the theme's section framework when present so the
   * editor's select/deselect/unload events work; otherwise init directly.
   * ------------------------------------------------------------------ */
  function boot() {
    if (window.theme && window.theme.sections && typeof window.theme.sections.register === 'function') {
      window.theme.sections.register('consultation-funnel', ConsultationFunnel);
      return;
    }
    document.querySelectorAll('[data-section-type="consultation-funnel"]').forEach(function (el) {
      new ConsultationFunnel(el);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  window.BlincConsultationFunnel = ConsultationFunnel;
})();
